/**
 * Trend line utilities for dashboard charts.
 * Compute moving averages for trend overlay on bar charts.
 */

const DEFAULT_WINDOW_SIZE = 7

/**
 * Compute a moving average for a numeric series.
 * @param {number[]} values - Array of numeric values
 * @param {number} [windowSize=7] - Moving average window size
 * @returns {number[]} Array of moving average values (null where insufficient data)
 */
export function computeMovingAverage(values, windowSize = DEFAULT_WINDOW_SIZE) {
  if (!values || values.length === 0) return []
  const w = Math.min(Math.max(1, windowSize), values.length)
  const result = []
  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - w + 1)
    const slice = values.slice(start, i + 1)
    const sum = slice.reduce((a, b) => a + (Number(b) || 0), 0)
    result.push(slice.length > 0 ? sum / slice.length : null)
  }
  return result
}

/**
 * Enrich chart data with trend values for specified keys.
 * @param {Object[]} data - Chart data array
 * @param {string[]} keys - Data keys to compute trend for (e.g. ['impressions', 'engagements'])
 * @param {number} [windowSize] - Moving average window (default: min(7, data.length))
 * @returns {Object[]} Data with trend keys added (e.g. impressionsTrend, engagementsTrend)
 */
export function addTrendToData(data, keys, windowSize) {
  if (!data || data.length === 0 || !keys || keys.length === 0) return data
  const effectiveWindow = windowSize ?? Math.min(DEFAULT_WINDOW_SIZE, Math.max(1, data.length - 1))
  const enriched = data.map((row) => ({ ...row }))
  keys.forEach((key) => {
    const values = data.map((d) => Number(d[key]) || 0)
    const trends = computeMovingAverage(values, effectiveWindow)
    trends.forEach((val, i) => {
      enriched[i][`${key}Trend`] = val
    })
  })
  return enriched
}
